import { useEffect, useRef, useState, type FC } from 'react';
import { Button, Box, TextField, Link } from '@mui/material';
import '../TrialWelcome/trial_welcome.css';
import './paywall.css';
import CloseIcon from '../../assets/images/CloseIcon.png';
import GreenCheckmark from '../../assets/images/GreenCheckmark.png';
import { numTrialDays } from 'Billing/Constants';
import {
  PLAN_IDS,
  PLAN_DETAILS,
  formatPrice,
  savingsPercent,
  type PlanId,
  type PricingCatalog,
} from 'Billing/Catalog';
import { usePricingCatalog } from 'Billing/usePricingCatalog';
import axios from 'axios';
import WelcomeButton from '../TrialWelcome/WelcomeButton';
import LogOutButton from 'Components/LogOutButton';
import { SURVEY_LINK, isCapacitorNative } from 'Utils/Constants';
import { useSelector, useDispatch } from 'react-redux';
import { MainAppReducer } from 'Types';
import { useAuthWrapperContext } from 'Contexts/AuthWrapperContext';
import {
  IAP_VERIFICATION_EVENT,
  armIAPReceiptVerification,
  clearIAPReceiptVerificationArm,
  ensureIAPReady,
  iapProductIdForPlan,
  nativeIAPStorePlatform,
  restoreIAPPurchases,
} from 'iap/initPurchases';
import { selectRecurringOffer, type RecurringOffer } from 'iap/Offers';
import { appStoreAccountToken } from 'iap/accountToken';
import { reportError } from 'Utils/reportError';

const baseUrl = `${process.env.REACT_APP_BACKEND_URL}/api/v1/`;
interface PaywallProps {
  setShowPaywall: React.Dispatch<React.SetStateAction<boolean>>;
  setShowPayment: React.Dispatch<React.SetStateAction<boolean>>;
  setPlanTypeSelected: React.Dispatch<React.SetStateAction<string>>;
  exitAllowed: boolean;
  discountCodeValid: boolean | null;
  setDiscountCodeValid: React.Dispatch<React.SetStateAction<boolean | null>>;
  discountCode: string;
  setDiscountCode: React.Dispatch<React.SetStateAction<string>>;
  showDiscountCode: boolean;
  setShowDiscountCode: React.Dispatch<React.SetStateAction<boolean>>;
  /** Only supplied by the development-only visual route; all purchase controls stay disabled. */
  previewCatalog?: PricingCatalog;
}

const errorMessage = (error: unknown) => {
  const response = (error as { response?: { data?: { message?: string } } })?.response;
  return (
    response?.data?.message ||
    (error as Error)?.message ||
    'Unable to complete your request. Please try again.'
  );
};

const Paywall: FC<PaywallProps> = ({
  setShowPaywall,
  exitAllowed,
  discountCodeValid,
  setDiscountCodeValid,
  discountCode,
  setDiscountCode,
  showDiscountCode,
  setShowDiscountCode,
  previewCatalog,
}) => {
  const { userData } = useSelector((state: MainAppReducer) => state.mainAppReducer);
  const dispatch = useDispatch();
  const currentAccount = useRef(userData?.user_id);
  currentAccount.current = userData?.user_id;
  const mounted = useRef(true);
  useEffect(() => {
    mounted.current = true;
    return () => {
      mounted.current = false;
    };
  }, []);
  const accountIsCurrent = () => mounted.current && currentAccount.current === userData?.user_id;

  const { currentAuthToken } = useAuthWrapperContext();
  const platform = nativeIAPStorePlatform();
  const native = isCapacitorNative();
  const webPricing = usePricingCatalog(!native, previewCatalog);
  const [nativeOffers, setNativeOffers] = useState<Partial<Record<PlanId, RecurringOffer>>>({});
  const [nativeLoading, setNativeLoading] = useState(native);
  const [nativeRevision, setNativeRevision] = useState(0);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [purchasingPlan, setPurchasingPlan] = useState<string | null>(null);
  const busy = useRef(false);
  const [validatingCode, setValidatingCode] = useState(false);

  useEffect(() => {
    const receiveError = (event: Event) => {
      const detail = (event as CustomEvent<{ message?: string }>).detail;
      if (detail?.message) setError(detail.message);
    };
    window.addEventListener(IAP_VERIFICATION_EVENT, receiveError);
    return () => window.removeEventListener(IAP_VERIFICATION_EVENT, receiveError);
  }, []);

  useEffect(() => {
    if (!native) return;
    let canceled = false;
    setNativeLoading(true);
    setNativeOffers({});
    setError(null);
    (async () => {
      try {
        if (!platform) throw new Error('Purchases are unavailable on this device.');
        const token = await currentAuthToken();
        const { data: options } = await axios.get<{
          ready: boolean;
          accountId?: string;
          reason?: string;
        }>(`${baseUrl}${platform === 'google' ? 'google' : 'receipts'}/purchase-options`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!options.ready || (platform === 'google' && !options.accountId)) {
          throw new Error(
            options.reason ||
              'Store subscriptions are temporarily unavailable. Please try again later.',
          );
        }
        const Cdv = await ensureIAPReady(userData, dispatch, currentAuthToken);
        if (!Cdv) throw new Error('The store is unavailable. Please try again.');
        const storePlatform =
          platform === 'apple' ? Cdv.Platform.APPLE_APPSTORE : Cdv.Platform.GOOGLE_PLAY;
        const offers: Partial<Record<PlanId, RecurringOffer>> = {};
        for (const plan of PLAN_IDS) {
          offers[plan] = selectRecurringOffer(
            Cdv.store.get(iapProductIdForPlan(plan, platform), storePlatform),
            plan,
          );
        }
        if (!canceled) {
          setNativeOffers(offers);
          if (PLAN_IDS.some((plan) => !offers[plan]))
            setError('Some subscriptions are unavailable from the store. Please try again later.');
        }
      } catch (cause) {
        if (!canceled) setError(errorMessage(cause));
      } finally {
        if (!canceled) setNativeLoading(false);
      }
    })();
    return () => {
      canceled = true;
    };
  }, [native, platform, nativeRevision, currentAuthToken, dispatch, userData?.user_id]);

  const validateDiscountCode = async () => {
    if (previewCatalog || validatingCode || purchasingPlan) return;
    setValidatingCode(true);
    setError(null);
    try {
      const authToken = await currentAuthToken();
      await axios.post(
        `${baseUrl}stripe/validate-coupon-code`,
        { code: discountCode.trim() },
        {
          headers: { Authorization: `Bearer ${authToken}` },
          timeout: 15000,
        },
      );
      setDiscountCode(discountCode.trim());
      setDiscountCodeValid(true);
    } catch {
      setDiscountCodeValid(false);
    } finally {
      setValidatingCode(false);
    }
  };

  const submitPlanSelect = async (plan: PlanId) => {
    if (busy.current || previewCatalog) return;
    busy.current = true;
    setPurchasingPlan(plan);
    setError(null);
    setMessage(null);
    try {
      if (native) {
        if (!platform || !nativeOffers[plan])
          throw new Error('This subscription is unavailable. Please reload prices.');
        const Cdv = await ensureIAPReady(userData, dispatch, currentAuthToken);
        if (!Cdv) throw new Error('The store is unavailable. Please try again.');
        const storePlatform =
          platform === 'apple' ? Cdv.Platform.APPLE_APPSTORE : Cdv.Platform.GOOGLE_PLAY;
        const currentOffer = selectRecurringOffer(
          Cdv.store.get(iapProductIdForPlan(plan, platform), storePlatform),
          plan,
        );
        const displayed = nativeOffers[plan]!;
        if (
          !currentOffer ||
          currentOffer.phase.priceMicros !== displayed.phase.priceMicros ||
          currentOffer.phase.currency !== displayed.phase.currency ||
          currentOffer.offer.id !== displayed.offer.id
        ) {
          setNativeRevision((value) => value + 1);
          throw new Error(
            'Store pricing changed. Please review the updated prices and select again.',
          );
        }
        // Recheck immediately before opening the store sheet: another device may have subscribed.
        const token = await currentAuthToken();
        const { data: purchaseOptions } = await axios.get<{
          ready: boolean;
          accountId?: string;
          reason?: string;
        }>(`${baseUrl}${platform === 'google' ? 'google' : 'receipts'}/purchase-options`, {
          headers: { Authorization: `Bearer ${token}` },
          timeout: 15000,
        });
        if (!purchaseOptions.ready || (platform === 'google' && !purchaseOptions.accountId)) {
          throw new Error(
            purchaseOptions.reason ||
              'Please restore or manage your existing subscription before purchasing.',
          );
        }
        if (!accountIsCurrent()) return;
        // An App Store purchase that does not carry this account's id could
        // never be matched to it if the app were interrupted after payment.
        // Refuse before any money moves rather than take it unattributed.
        if (platform === 'apple' && !appStoreAccountToken(userData)) {
          throw new Error(
            'We could not confirm your account for this purchase. Please log out, log back in and try again.',
          );
        }
        armIAPReceiptVerification(iapProductIdForPlan(plan, platform));
        const orderError = await Cdv.store.order(
          currentOffer.offer,
          platform === 'google'
            ? { googlePlay: { accountId: purchaseOptions.accountId } }
            : undefined,
        );
        if (!accountIsCurrent()) return;
        if (orderError) {
          clearIAPReceiptVerificationArm();
          if (
            Cdv.ErrorCode?.PAYMENT_CANCELLED !== undefined &&
            orderError.code === Cdv.ErrorCode.PAYMENT_CANCELLED
          )
            return;
          throw new Error(orderError.message || 'The purchase was not completed.');
        }
        setMessage(
          'Your store will confirm the purchase. Pending payments or approvals unlock access once confirmed.',
        );
      } else {
        if (!webPricing.catalog) throw new Error('Please reload subscription prices.');
        const token = await currentAuthToken();
        const { data } = await axios.post<{ url?: string }>(
          `${baseUrl}stripe/checkout-session`,
          {
            plan,
            code: discountCodeValid ? discountCode.trim() : null,
          },
          { headers: { Authorization: `Bearer ${token}` }, timeout: 20000 },
        );
        if (!data.url || !data.url.startsWith('https://checkout.stripe.com/'))
          throw new Error('Checkout is unavailable. Please try again.');
        window.location.assign(data.url);
      }
    } catch (cause) {
      reportError(cause, 'billing.purchase');
      setError(errorMessage(cause));
    } finally {
      busy.current = false;
      setPurchasingPlan(null);
    }
  };

  const restore = async () => {
    if (busy.current) return;
    busy.current = true;
    setPurchasingPlan('restore');
    setError(null);
    setMessage(null);
    try {
      const active = await restoreIAPPurchases(userData, dispatch, currentAuthToken);
      if (!accountIsCurrent()) return;
      if (active) setShowPaywall(false);
      setMessage(
        active
          ? 'Your subscription has been restored.'
          : 'Restore requested. An active purchase on this store account will unlock access after verification. If none appears, check your store subscriptions.',
      );
    } catch (cause) {
      setError(errorMessage(cause));
    } finally {
      busy.current = false;
      setPurchasingPlan(null);
    }
  };

  const loading = native ? nativeLoading : webPricing.loading;
  const displayedError = error || webPricing.error;
  const reload = () => {
    setError(null);
    if (native) setNativeRevision((value) => value + 1);
    else webPricing.reload();
  };

  return (
    <>
      <Box className="modal-outer" />
      <Box
        className="modal-inner2 paywall-modal"
        role="dialog"
        aria-modal="true"
        aria-label="Choose a subscription"
      >
        {exitAllowed && (
          <Button
            onClick={() => setShowPaywall(false)}
            aria-label="Close"
            sx={{ position: 'absolute', top: 15, right: 15 }}
          >
            <img src={CloseIcon} alt="" />
          </Button>
        )}
        <Box className="paywall-desc-container">
          <h3 className="paywall-desc-title">Your free trial has ended.</h3>
          <p className="paywall-desc-text" style={{ marginTop: '5px', lineHeight: '133%' }}>
            We hope you enjoyed your {numTrialDays}-day free trial! Please select a payment plan to
            continue playing. We’d also love to hear{' '}
            <Link href={SURVEY_LINK} target="_blank" rel="noreferrer" className="link">
              any feedback you have
            </Link>
            .
          </p>
        </Box>
        <Box className="plan-container" aria-busy={loading}>
          {PLAN_IDS.map((id) => {
            const details = PLAN_DETAILS[id];
            const webPlan = webPricing.catalog?.plans.find((plan) => plan.id === id);
            const phase = nativeOffers[id]?.phase;
            const amount = native
              ? phase
                ? phase.priceMicros / 1000000
                : undefined
              : webPlan
                ? webPlan.amountCents / 100
                : undefined;
            const currency = native ? phase?.currency : webPricing.catalog?.currency;
            const monthlyPhase = nativeOffers.monthly?.phase;
            const monthly = native
              ? monthlyPhase?.currency === currency
                ? (monthlyPhase?.priceMicros ?? 0) / 1000000
                : 0
              : (webPricing.catalog?.plans.find((plan) => plan.id === 'monthly')?.amountCents ??
                  0) / 100;
            const savings = amount ? savingsPercent(amount, monthly, details.months) : 0;
            const price = native
              ? phase?.price
              : amount !== undefined && currency
                ? formatPrice(amount, currency)
                : undefined;
            return (
              <Box className="plan" key={id} data-plan={id}>
                {savings > 0 && (
                  <Box className="discount-percent">
                    <p className="discount-percent-text">{savings}% off</p>
                  </Box>
                )}
                <Box className="plan-card">
                  <Box className="plan-title">
                    <p className="plan-text-desc">{details.title}</p>
                    <p className="plan-text-price">
                      {price ?? (loading ? 'Loading…' : 'Unavailable')}
                    </p>
                    <p className="plan-text-time">per {details.frequency}</p>
                    {details.months > 1 && amount && currency && (
                      <p className="plan-text-equivalent">
                        {formatPrice(amount / details.months, currency)} per month equivalent
                      </p>
                    )}
                    {id !== 'monthly' && (
                      <Box className="lesson-check" sx={{ width: 'unset' }}>
                        <img src={GreenCheckmark} className="green-check-img" alt="" />
                        <p className="plan-text-time">
                          {id === 'annual' ? 'Two 30min lessons' : '30min lesson'}
                        </p>
                      </Box>
                    )}
                    {id === 'annual' && (
                      <Box className="lesson-check">
                        <img src={GreenCheckmark} className="green-check-img" alt="" />
                        <p className="plan-text-time">MIDI cable</p>
                      </Box>
                    )}
                  </Box>
                  <WelcomeButton
                    className="plan-select-button"
                    onClick={() => submitPlanSelect(id)}
                    disabled={
                      !!purchasingPlan || validatingCode || !price || loading || !!previewCatalog
                    }
                  >
                    {purchasingPlan === id ? 'LOADING…' : 'SELECT'}
                  </WelcomeButton>
                </Box>
              </Box>
            );
          })}
        </Box>
        <p className="paywall-desc-text billing-terms">
          Automatically renews until canceled. Savings compare with the monthly plan.{' '}
          {native
            ? 'The store confirms your price and billing terms.'
            : 'Prices are in USD; applicable tax and coupon terms are shown at checkout.'}
        </p>
        {displayedError && (
          <Box className="iap-error" role="alert">
            {displayedError}{' '}
            <Button onClick={reload} disabled={loading || !!purchasingPlan}>
              Retry
            </Button>
          </Box>
        )}
        {message && (
          <p className="paywall-desc-text" role="status">
            {message}
          </p>
        )}
        {native ? (
          <Button onClick={restore} disabled={!!purchasingPlan}>
            {purchasingPlan === 'restore' ? 'RESTORING…' : 'Restore purchases'}
          </Button>
        ) : (
          <>
            {!showDiscountCode && (
              <Button onClick={() => setShowDiscountCode(true)} className="discount-code">
                Got a coupon code?
              </Button>
            )}
            {showDiscountCode && (
              <Box className="discount-container">
                <TextField
                  className={`discount-input ${discountCodeValid ? 'valid-discount' : ''}`}
                  label="Enter Coupon Code"
                  value={discountCode}
                  disabled={validatingCode || !!purchasingPlan}
                  onChange={(event) => {
                    setDiscountCode(event.target.value);
                    setDiscountCodeValid(null);
                  }}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter' && !validatingCode) validateDiscountCode();
                  }}
                />
                <Button
                  onClick={validateDiscountCode}
                  className="discount-button"
                  disabled={validatingCode || !discountCode.trim() || !!previewCatalog}
                >
                  {validatingCode ? 'Checking…' : 'Apply'}
                </Button>
                {discountCodeValid === false && (
                  <span role="alert" className="iap-error">
                    Invalid or unavailable coupon.
                  </span>
                )}
              </Box>
            )}
            {discountCodeValid && (
              <p className="paywall-desc-text">
                Coupon accepted. Your discount and its duration will be shown at checkout.
              </p>
            )}
          </>
        )}
        <Box className="logout-container">
          <LogOutButton />
        </Box>
      </Box>
    </>
  );
};
export default Paywall;
